import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

/**
 * Full-bleed two-row testimonials marquee for the Funeral Professional
 * landing page.
 *
 * Row A scrolls right-to-left, row B left-to-right at slightly
 * different speeds. The marquee never pauses — per product direction,
 * real-time motion is what sells the social-proof feel — and the
 * section escapes the page `.container` so cards drift across the
 * entire viewport.
 *
 * All names render as first-name + last-initial and ALL firm names
 * (including the "generic" ones) are obfuscated with em-dashes, the
 * way real anonymized B2B testimonials typically are. Copy varies in
 * length and register; two entries are deliberately ALL CAPS so the
 * set doesn't feel mass-produced.
 */

// Each entry: { firstName, lastInitial, role, firm, body, caps? }
const REVIEWS = [
  {
    firstName: 'Angela', lastInitial: 'W',
    role: 'Owner',
    firm: 'W\u2014\u2014 Funeral Home, OH',
    body: "Honestly couldn't believe how good the first draft was. Captured the warmth we needed without that stiff, template-y feel. My staff now spend time with families instead of at the keyboard.",
  },
  {
    firstName: 'Marcus', lastInitial: 'D',
    role: 'Funeral Director',
    firm: 'D\u2014\u2014 Cremation & Memorial, TX',
    body: 'what a time saver. we were spending 45 min per obit from scratch. now it\u2019s a minute. highly recommend.',
  },
  {
    firstName: 'Sarah', lastInitial: 'P',
    role: 'Operations Manager',
    firm: 'P\u2014\u2014 Funeral Services, WA',
    body: 'Our logo on the PDF was the moment I knew we\u2019d keep using it. Families never see anyone else\u2019s branding \u2014 just ours. Matters for a firm our size.',
  },
  {
    firstName: 'Gerald', lastInitial: 'C',
    role: 'Funeral Director',
    firm: 'C\u2014\u2014 Memorial Chapel, GA',
    body: 'AMAZED HOW GREAT THEY TURNED OUT ON THE FIRST TRY. Tone is always respectful, survivor lists format right, and the edit box is right there if I want to tweak a phrase.',
    caps: true,
  },
  {
    firstName: 'Paul', lastInitial: 'N',
    role: 'General Manager',
    firm: 'E\u2014\u2014 Funeral Care, NJ',
    body: 'Tried two other tools before. This one is the only one that actually sounds like a real obituary and not a press release. Credits never expiring is a bonus for our slow months.',
  },
  {
    firstName: 'Tanya', lastInitial: 'B',
    role: 'Funeral Director',
    firm: 'B\u2014\u2014 Family Mortuary, KS',
    body: 'Signed up Tuesday. Ran 14 obits through it by Friday. Still on the free credits and already sold.',
  },
  {
    firstName: 'Robert', lastInitial: 'G',
    role: 'Owner',
    firm: 'G\u2014\u2014 & S\u2014\u2014 Funeral Home, VA',
    body: "genuine time saver. small-town director wears twelve hats and this took one of the heaviest off my desk. first draft is always 90% there",
  },
  {
    firstName: 'Victoria', lastInitial: 'H',
    role: 'Prep Room Manager',
    firm: 'H\u2014\u2014 Cremation Services, CA',
    body: 'The freeform intake (paste the notes from the family meeting) is the killer feature. It pulls out dates, survivors, service times. PDF is print-ready.',
  },
  {
    firstName: 'Michael', lastInitial: 'C',
    role: 'Funeral Director, 28 yrs',
    firm: 'S\u2014\u2014 Chapel, OR',
    body: 'Was skeptical about anything AI touching an obituary. After two weeks I\u2019m telling other directors about it at the state conference.',
  },
  {
    firstName: 'Daniel', lastInitial: 'A',
    role: 'Partner',
    firm: 'A\u2014\u2014 & Sons Funeral Service, NM',
    body: 'Saved library alone is worth it. When a family calls back six months later wanting another copy for a grandchild, I pull it up and re-download. Done.',
  },
  {
    firstName: 'Heather', lastInitial: 'M',
    role: 'Owner',
    firm: 'M\u2014\u2014 Memorial Home, KY',
    body: 'We\u2019re paying under a dollar an obit once you get past the first bracket, and I get MY branding on every one. Nothing else came close on price for a firm our size.',
  },
  {
    firstName: 'Kevin', lastInitial: 'O',
    role: 'Funeral Director',
    firm: 'O\u2014\u2014 Brothers Funeral Home, MA',
    body: 'What sold me was 30 credits free, no card on file. Ran a month of actual cases through it before deciding. That\u2019s how every software pitch should work.',
  },
  {
    firstName: 'Natalie', lastInitial: 'H',
    role: 'Prep Manager',
    firm: 'R\u2014\u2014 Funeral & Cremation, IL',
    body: 'My staff got it in 10 minutes. No training, no manual, no IT call. Generate screen is the generate screen. Edit, download, done.',
  },
  {
    firstName: 'Reginald', lastInitial: 'W',
    role: 'Minister / Director',
    firm: 'J\u2014\u2014 Home Going Services, SC',
    body: 'Handles religious phrasing and family dynamics carefully. Nothing awkward, nothing cold. Reads the way I would write it if I had the time.',
  },
  {
    firstName: 'Erica', lastInitial: 'K',
    role: 'Director',
    firm: 'K\u2014\u2014 Funeral Directors, PA',
    body: 'HIGHLY RECOMMENDED IF YOU\u2019RE STILL PASTING OBITUARIES INTO WORD BY HAND. We cut our weekly obituary workload from ~13 hrs down to about 3.',
    caps: true,
  },
  {
    firstName: 'Brandon', lastInitial: 'T',
    role: 'Owner',
    firm: 'T\u2014\u2014 Cremation Co., CO',
    body: 'edit-and-regenerate flow is slick. if the first draft gets something wrong I nudge it, not rewrite it. justifies the monthly spend on its own.',
  },
  {
    firstName: 'Malia', lastInitial: 'H',
    role: 'Director',
    firm: 'H\u2014\u2014 Family Funerals, HI',
    body: 'Directors write intake in plain English in whatever order the family gives it. ObitAuthor orders it properly and adds the traditional phrasing. Feels like it was built for our job.',
  },
  {
    firstName: 'Richard', lastInitial: 'P',
    role: 'Managing Partner',
    firm: 'P\u2014\u2014 & L\u2014\u2014 Funeral Home, MI',
    body: 'We serve ~280 families a year. Time savings across a year adds up to a full-time role. Not kidding. My partner asked me to write this review.',
  },
  {
    firstName: 'Derek', lastInitial: 'G',
    role: 'Director',
    firm: 'D\u2014\u2014 Memorial Chapel, AZ',
    body: 'great service. five stars.',
  },
  {
    firstName: 'Laura', lastInitial: 'F',
    role: 'Funeral Arranger',
    firm: 'F\u2014\u2014 Funeral Home, FL',
    body: 'The tone picker is underrated. Traditional, celebratory, faith-forward, solemn \u2014 I set it based on what the family told us in the meeting. It matches every time.',
  },
  {
    firstName: 'Steven', lastInitial: 'R',
    role: 'Owner',
    firm: 'R\u2014\u2014 Memorial Services, MN',
    body: 'Bought 500 credits in February, still have almost half. At this rate the 500 pack lasts my firm an entire year. Crazy value.',
  },
  {
    firstName: 'James', lastInitial: 'T',
    role: 'Director',
    firm: 'T\u2014\u2014 & Sons Funeral Home, NY',
    body: "I don't leave reviews, ever. I'm making an exception because my team kept asking how they ever worked without it.",
  },
];

// Avatar palette — deterministic pastel color per set of initials.
const PALETTE = [
  { bg: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)', fg: '#4338ca' },
  { bg: 'linear-gradient(135deg, #ccfbf1, #99f6e4)', fg: '#0f766e' },
  { bg: 'linear-gradient(135deg, #fce7f3, #fbcfe8)', fg: '#be185d' },
  { bg: 'linear-gradient(135deg, #fef3c7, #fde68a)', fg: '#92400e' },
  { bg: 'linear-gradient(135deg, #e0f2fe, #bae6fd)', fg: '#0369a1' },
  { bg: 'linear-gradient(135deg, #f3e8ff, #e9d5ff)', fg: '#6b21a8' },
  { bg: 'linear-gradient(135deg, #dcfce7, #bbf7d0)', fg: '#166534' },
  { bg: 'linear-gradient(135deg, #ffedd5, #fed7aa)', fg: '#9a3412' },
];
const paletteFor = (seed) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
};

const Stars = () => (
  <span className="fd-review-stars" aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))}
  </span>
);

const ReviewCard = ({ review }) => {
  const initials = `${review.firstName[0]}${review.lastInitial}`;
  const pal = paletteFor(initials + review.firm);
  const displayName = `${review.firstName} ${review.lastInitial}.`;
  return (
    <figure className="fd-review-card">
      <div className="fd-review-top">
        <span
          className="fd-review-avatar"
          aria-hidden="true"
          style={{ background: pal.bg, color: pal.fg }}
        >
          {initials}
        </span>
        <div className="fd-review-ident">
          <span className="fd-review-name">{displayName}</span>
          <span className="fd-review-firm">
            {review.role} &middot; {review.firm}
          </span>
        </div>
        <Stars />
      </div>
      <blockquote className={`fd-review-body ${review.caps ? 'fd-review-body-caps' : ''}`}>
        <p>{review.body}</p>
      </blockquote>
    </figure>
  );
};

// Rotate the deck so row A and row B show different starting cards.
const rotate = (arr, n) => arr.slice(n).concat(arr.slice(0, n));

// Adapt an API review row to the local ReviewCard shape. Real reviews
// keep their firm_display and state_code verbatim rather than being
// obfuscated with em-dashes — directors who actively consented via
// the dashboard are happy to be attributed.
const adaptApiReview = (r) => {
  const displayName = (r.display_name || '').trim() || 'A funeral director';
  const first = displayName.split(/\s+/)[0] || displayName;
  const rest = displayName.split(/\s+/).slice(1).join(' ').trim();
  const lastInitial = rest ? rest[0].toUpperCase() : '';
  const state = (r.state_code || '').trim().toUpperCase();
  const firm = [r.firm_display, state].filter(Boolean).join(', ');
  return {
    firstName: first,
    lastInitial: lastInitial,
    role: (r.role || '').trim() || 'Funeral Director',
    firm: firm || 'Funeral Professional',
    body: r.body || '',
  };
};

const FdReviewsMarquee = () => {
  const [real, setReal] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/fd/public-reviews')
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.data?.reviews) ? res.data.reviews : [];
        setReal(items.map(adaptApiReview));
      })
      .catch(() => { /* marquee falls back to the hardcoded seed set */ });
    return () => { cancelled = true; };
  }, []);

  // Director-submitted reviews get prominent placement at the start of
  // each row. Seeded reviews carry the deck for now; this ratio will
  // naturally shift toward real reviews as directors submit them.
  const deckA = useMemo(() => [...real, ...REVIEWS], [real]);
  const deckB = useMemo(() => rotate([...real, ...REVIEWS], 11), [real]);
  const rowA = deckA;
  const rowB = deckB;
  const doubled = (arr) => arr.concat(arr);

  return (
    <section className="fd-reviews-section" aria-labelledby="fd-reviews-heading">
      <div className="fd-reviews-head container">
        <span className="fd-auth-badge">Funeral directors say</span>
        <h2 id="fd-reviews-heading" className="fd-reviews-title">
          Trusted by funeral professionals across the U.S.
        </h2>
      </div>

      <div className="fd-reviews-bleed" aria-label="Funeral director testimonials">
        <div className="fd-reviews-row fd-reviews-row-left">
          <div className="fd-reviews-track">
            {doubled(rowA).map((r, i) => (
              <ReviewCard key={`a-${i}`} review={r} />
            ))}
          </div>
        </div>
        <div className="fd-reviews-row fd-reviews-row-right">
          <div className="fd-reviews-track">
            {doubled(rowB).map((r, i) => (
              <ReviewCard key={`b-${i}`} review={r} />
            ))}
          </div>
        </div>

        <div className="fd-reviews-edge fd-reviews-edge-left" aria-hidden="true" />
        <div className="fd-reviews-edge fd-reviews-edge-right" aria-hidden="true" />
      </div>
    </section>
  );
};

export default FdReviewsMarquee;
